import { useEffect, useRef, useState } from 'preact/hooks';
import { apiFetch } from '../api/client';
import { ReconnectSquare } from './ReconnectSquare';
import { ASSISTANT_NAME } from '../brand.js';

export function SquareLocationPicker({ onConfirmed, guided = false }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const alive = useRef(false);

  async function load() {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError('');
    setData(null);
    try {
      const result = await apiFetch('/v1/owner/square/locations');
      if (!alive.current) return;
      if (!result || !Array.isArray(result.locations)
          || !Number.isInteger(result.selection_version) || result.selection_version < 0
          || typeof result.locked !== 'boolean'
          || !(result.selected_location_id == null || typeof result.selected_location_id === 'string')
          || !result.locations.every(location => location && typeof location.id === 'string' && location.id
            && typeof location.name === 'string' && location.name
            && (location.address == null || typeof location.address === 'string'))) {
        throw new Error('Square locations could not be verified. Refresh locations to try again.');
      }
      setData(result);
      setSelected(result.selected_location_id || (result.locations.length === 1 ? result.locations[0].id : ''));
    } catch (err) {
      if (alive.current) setError(err.message || 'Could not load Square locations.');
    } finally {
      pending.current = false;
      if (alive.current) setBusy(false);
    }
  }

  useEffect(() => {
    alive.current = true;
    load();
    return () => { alive.current = false; };
  }, []);

  async function confirm(event) {
    event.preventDefault();
    if (pending.current || !data || !selected) return;
    pending.current = true;
    setBusy(true);
    setError('');
    try {
      const result = await apiFetch('/v1/owner/square/location', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ location_id: selected, expected_selection_version: data.selection_version }),
      });
      if (result.selected_location_id !== selected || result.confirmed !== true
          || result.selection_version !== data.selection_version + 1) {
        throw new Error('The saved location could not be confirmed. Refresh before trying again.');
      }
      if (alive.current) {
        setData(null);
        onConfirmed?.();
      }
    } catch (err) {
      if (alive.current) {
        setData(null); // An uncertain save must reload its server version.
        setError(err.message || 'The location save could not be confirmed. Refresh before trying again.');
      }
    } finally {
      pending.current = false;
      if (alive.current) setBusy(false);
    }
  }

  const choices = data?.locations.filter(location => !data.locked || location.id === data.selected_location_id) || [];
  const canConfirm = choices.some(location => location.id === selected);
  const buttonStyle = { minHeight: '44px', padding: '0.75rem 1rem', borderRadius: '10px', font: 'inherit', cursor: 'pointer' };
  return <section style={{ maxWidth: '440px', margin: '0 auto', padding: '2rem 1.25rem' }}>
    <h1 style={{ fontSize: '1.65rem', marginBottom: '0.75rem' }}>Choose your Square location</h1>
    <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>{ASSISTANT_NAME} uses this location’s services, team and appointments. Your subscription covers one location.</p>
    {data?.locked
      ? <p>Setup has already started. You can confirm the current location here; contact support to move to another location.</p>
      : <p>You can change this choice until setup begins. Online booking must be enabled for the location in Square.</p>}
    {error && <p role="alert" style={{ color: 'var(--coral)' }}>{error}</p>}
    {busy && <p role="status">Checking your Square location…</p>}
    {data && choices.length > 0 && <form onSubmit={confirm}>
      <label for="square-location" style={{ display: 'block', marginBottom: '0.5rem' }}>Salon location</label>
      <select id="square-location" value={selected} disabled={busy} onChange={event => setSelected(event.currentTarget.value)} style={{ width: '100%', minHeight: '48px', padding: '0.65rem', marginBottom: '1rem', font: 'inherit' }}>
        <option value="" disabled>Choose a location</option>
        {choices.map(location => <option key={location.id} value={location.id}>{location.name}{location.address ? ` — ${location.address}` : ''}</option>)}
      </select>
      <button type="submit" disabled={busy || !canConfirm} style={{ ...buttonStyle, width: '100%', background: 'var(--sage)', color: 'white', border: 'none' }}>Confirm location</button>
    </form>}
    {data && choices.length === 0 && <p role="status">No eligible active location was found. Check your Square locations or <a href="mailto:support@gojonathan.ai">contact support</a>.</p>}
    {(!guided || error || (data && choices.length === 0)) && <button type="button" disabled={busy} onClick={load} style={{ ...buttonStyle, marginTop: '1rem', background: 'white', border: '1px solid var(--border-subtle)' }}>Refresh locations</button>}
    {(!guided || error || (data && choices.length === 0)) && <ReconnectSquare />}
  </section>;
}
